/**
 * Uppy + Transloadit/Transforge setup shared by both asset upload hooks:
 * `useTransloaditUpload` (Uppy's own modal, or inline for the smallapp file field) and
 * `useUploadDialog` (Uppy mounted inside `<UploadFilesDialog>`).
 *
 * Owns what does not depend on how the Dashboard is shown: fetching upload options (with the
 * prefetch cache and the Transforge reachability fallback), restrictions, locale, the Transloadit
 * plugin, cloud providers, client-side file checks, the rename sync before upload, and the
 * per-file success / failure wiring. Each hook adds its own Dashboard mode and `complete` handling.
 *
 * scio1 source: imports/ui/services/index.js → openUploadModal(), processUploads()
 * scio2 reference: optisigns-portal-common → transloadit.service.ts
 */
import type { ApolloClient } from '@apollo/client';
import { GET_FILE_UPLOAD_OPTIONS } from '@scio3/graphql-client';
import type { TFunction } from 'i18next';
import Uppy from '@uppy/core';
import type { UppyFile, Restrictions } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import type { DashboardOptions } from '@uppy/dashboard';
import Transloadit, { COMPANION_URL, COMPANION_ALLOWED_HOSTS } from '@uppy/transloadit';
import type { AssemblyOptions } from '@uppy/transloadit';
import Webcam from '@uppy/webcam';
import GoogleDrive from '@uppy/google-drive';
import Dropbox from '@uppy/dropbox';
import OneDrive from '@uppy/onedrive';
import en_US from '@uppy/locales/lib/en_US';
import de_DE from '@uppy/locales/lib/de_DE';
import fr_FR from '@uppy/locales/lib/fr_FR';
import es_ES from '@uppy/locales/lib/es_ES';
import it_IT from '@uppy/locales/lib/it_IT';
import nl_NL from '@uppy/locales/lib/nl_NL';
import pt_BR from '@uppy/locales/lib/pt_BR';
import sv_SE from '@uppy/locales/lib/sv_SE';
import { validateImageResolution, validateVideoBitrate } from './file-validation';
import { getUnsupportedFileTypeMessage } from './unsupported-file-type-message';
import { REJECTION_REASON, UPLOAD_SOURCE, type RejectionReason } from '../constants/upload-dialog';
import { reportAppError } from '@/lib/telemetry/faro';
import { useAuthStore } from '@/stores/auth-store';
import { classifyUploadError, isUploadCancelled, isUserFileUploadError } from '@/lib/upload-errors';

// --- Types ---

export interface UploadFileMeta {
  name: string;
  type: string;
  size: number;
  extension: string;
}

/** Share-link pages fetch options from a public endpoint instead of the default query. */
export type FetchUploadOptionsFn = () => Promise<{
  params?: Record<string, unknown>;
  bucket?: string;
  restrictions?: { maxFileSize?: number };
  signature?: string;
} | null>;

/** How a finished upload batch went: files uploaded vs. files that failed to upload. */
export interface UploadBatchResult {
  uploaded: number;
  failed: number;
}

/** Options both upload hooks take (the legacy hook adds its inline-only ones). */
export interface UploadOptions {
  /** Called per file on successful assembly upload. Caller handles save logic. */
  onUploadSuccess: (fileMeta: UploadFileMeta, assemblyId: string, bucket?: string) => void;
  /** Called when all files in a batch are done. A batch with failed uploads must not report
   *  success: the uploader has already shown the failure. */
  onAllComplete?: (result: UploadBatchResult) => void;
  /** Override for getUploadOptions query variables (e.g. replace asset flow). */
  uploadOptionsPayload?: Record<string, unknown>;
  /** Restrict to single file upload (for replace asset flow). */
  restrictToSingleFile?: boolean;
  /** Custom note text for the Dashboard (overrides default). */
  dashboardNote?: string;
  /** Called when the user closes the Dashboard modal without uploading. */
  onModalClosed?: () => void;
  /** Custom function to fetch upload options — overrides default GET_FILE_UPLOAD_OPTIONS query.
   *  Used by share link context to call public endpoint (getUploadOptionsForShareLink). */
  fetchUploadOptionsFn?: FetchUploadOptionsFn;
  /** Caller-supplied Uppy restrictions, merged over the backend-provided restrictions
   *  (caller keys win). Composes with `restrictToSingleFile`: an explicit
   *  `restrictions.maxNumberOfFiles` here wins over both `restrictToSingleFile` and the
   *  backend value. */
  restrictions?: Partial<Restrictions>;
}

/** A file the client-side checks refused after Uppy accepted it. */
export type ClientRejectionReason = Extract<
  RejectionReason,
  | typeof REJECTION_REASON.EMPTY
  | typeof REJECTION_REASON.RESOLUTION
  | typeof REJECTION_REASON.BITRATE
>;

// --- Constants ---

export const EMPTY_UPLOAD_PAYLOAD: Record<string, unknown> = {};

const UPPY_LOCALE_MAP: Record<string, typeof en_US> = {
  en: en_US,
  de: de_DE,
  fr: fr_FR,
  es: es_ES,
  it: it_IT,
  nl: nl_NL,
  pt: pt_BR,
  sv: sv_SE,
};

// --- Upload-options cache ---
// The default upload-options query (`{ payload: {} }`) is stable per session. Prefetching it
// when an upload UI is about to appear (e.g. the Canva wizard opening, before the user reaches
// the upload step) lets a later `open()` build Uppy with zero wait — no loading flash.
// `network-only` bypasses the Apollo cache, so we memoize the in-flight PROMISE here (short TTL,
// well under the signed-params expiry of 1 hour) and reuse it for the default payload.
const PREFETCH_TTL_MS = 60_000;
/** The entry carries the account it was signed for: logout does not reload the page, so the next
 *  sign-in in the same tab must never reuse another account's signed params (bucket, limits). */
let defaultOptionsCache: {
  promise: Promise<Record<string, unknown>>;
  at: number;
  accountId: string;
} | null = null;

/** The signed-in account, or null (signed out / not loaded yet: nothing is cached or reused). */
const currentAccountId = (): string | null => {
  const { me, company } = useAuthStore.getState();
  return me?.accountId ?? company?._id ?? null;
};

const isDefaultPayload = (payload: Record<string, unknown>): boolean =>
  !payload || Object.keys(payload).length === 0;

/** Fresh (within TTL) cached default-options promise for this account, or null. */
const freshCachedOptions = (
  now: number,
  accountId: string | null,
): Promise<Record<string, unknown>> | null =>
  defaultOptionsCache &&
  accountId &&
  defaultOptionsCache.accountId === accountId &&
  now - defaultOptionsCache.at < PREFETCH_TTL_MS
    ? defaultOptionsCache.promise
    : null;

/** Cache a default-options promise; a failed one is dropped so the next open retries cleanly. */
function cacheDefaultOptions(
  promise: Promise<Record<string, unknown>>,
  accountId: string | null,
): void {
  if (!accountId) return;
  promise.catch(() => {
    if (defaultOptionsCache?.promise === promise) defaultOptionsCache = null;
  });
  defaultOptionsCache = { promise, at: Date.now(), accountId };
}

/**
 * Warm the default (`{ payload: {} }`) upload-options query so a subsequent `open()`
 * builds Uppy with no network wait. Fire-and-forget; safe to call repeatedly (deduped within
 * the TTL). Trigger it when an upload UI is about to be reachable — e.g. `ConfigEngineDialog`
 * calls this on open when the manifest has a `file-upload` field.
 */
export function prefetchUploadOptions(client: ApolloClient): void {
  const accountId = currentAccountId();
  if (!accountId || freshCachedOptions(Date.now(), accountId)) return; // signed out, or warm
  cacheDefaultOptions(
    client
      .query<{ getFileUploadOptions?: Record<string, unknown> }>({
        query: GET_FILE_UPLOAD_OPTIONS,
        variables: { payload: {} },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const opts = data?.getFileUploadOptions;
        if (!opts) throw new Error('Failed to get upload options');
        return opts;
      }),
    accountId,
  );
}

/** Test-only: clear the module-level upload-options cache between cases. */
export function __resetUploadOptionsPrefetch(): void {
  defaultOptionsCache = null;
}

// --- Transforge reachability (client-side fallback) ---
// The backend hands out Transforge upload options (opts.endpoint set) whenever the
// account is on Transforge, but only the BROWSER can tell whether the Transforge
// gateway is actually reachable from here — the server sits inside the cluster and
// never sees the client's CORS / network / DNS / whitelist reality. So before we
// build Uppy against a Transforge endpoint, probe it; if it's unreachable we
// re-request options with forceTransloadit so we never create an assembly against a
// dead gateway (which would sit stuck in UPLOADING). Fail-closed: any probe failure
// (timeout, network, CORS, non-2xx) routes the upload to Transloadit.
const TF_HEALTH_TTL_MS = 60_000;
const TF_HEALTH_TIMEOUT_MS = 2500;
const tfReachableCache = new Map<string, { ok: boolean; at: number }>();

async function isTransforgeReachable(endpoint: string): Promise<boolean> {
  const cached = tfReachableCache.get(endpoint);
  if (cached && Date.now() - cached.at < TF_HEALTH_TTL_MS) return cached.ok;
  let ok = false;
  try {
    const url = new URL('/health', endpoint).toString();
    // Simple GET, no credentials → no CORS preflight; /health returns ACAO:* .
    const res = await fetch(url, {
      method: 'GET',
      signal: AbortSignal.timeout(TF_HEALTH_TIMEOUT_MS),
    });
    ok = res.ok;
  } catch {
    ok = false;
  }
  tfReachableCache.set(endpoint, { ok, at: Date.now() });
  return ok;
}

// --- Upload options ---

interface ResolveUploadOptionsArgs {
  /** Runs GET_FILE_UPLOAD_OPTIONS with this payload (the hook's network-only lazy query). */
  fetchOptions: (payload: Record<string, unknown>) => Promise<Record<string, unknown>>;
  payload: Record<string, unknown>;
  /** Share-link override; skips the default query, the cache and the Transforge probe. */
  fetchUploadOptionsFn?: FetchUploadOptionsFn;
  /** Also cache this open's own default-payload fetch, so a reopen within the TTL skips it. */
  cacheOwnFetch?: boolean;
  t: TFunction;
}

/** Upload options for one upload session: cached or fetched, then the Transforge fallback. */
export async function resolveUploadOptions({
  fetchOptions,
  payload,
  fetchUploadOptionsFn,
  cacheOwnFetch = false,
  t,
}: ResolveUploadOptionsArgs): Promise<Record<string, unknown>> {
  if (fetchUploadOptionsFn) {
    const opts = await fetchUploadOptionsFn();
    if (!opts) throw new Error(t('Failed to get upload options'));
    return opts as Record<string, unknown>;
  }

  const isDefault = isDefaultPayload(payload);
  const accountId = currentAccountId();
  let promise = isDefault ? freshCachedOptions(Date.now(), accountId) : null;
  if (!promise) {
    promise = fetchOptions(payload);
    if (isDefault && cacheOwnFetch) cacheDefaultOptions(promise, accountId);
  }
  let opts = await promise;

  // opts.endpoint set → backend picked Transforge. Verify it's reachable from the
  // browser; if not, fall back to the Transloadit shape for this upload session.
  // The gate also applies to a cached result, so a warmed default never skips the fallback.
  const tfEndpoint = opts.endpoint as string | undefined;
  if (tfEndpoint && !(await isTransforgeReachable(tfEndpoint))) {
    console.warn('[upload] Transforge gateway unreachable — falling back to Transloadit');
    // Not a user-visible failure (the fallback works), but one host failing is that host's
    // network and MANY hosts failing together is the gateway — only Loki can tell them apart.
    reportAppError({
      evt: 'upload_error',
      flow: 'upload-options',
      kind: 'unclassified',
      code: 'transforge_unreachable',
    });
    opts = await fetchOptions({ ...payload, forceTransloadit: true });
  }

  return opts;
}

/** Report a failed upload-options fetch (telemetry unless cancelled) and toast it. */
export function reportUploadOptionsError(
  err: unknown,
  t: TFunction,
  toastError: (msg: string) => void,
) {
  if (!isUploadCancelled(err))
    reportAppError({
      evt: 'upload_error',
      flow: 'upload-options',
      ...classifyUploadError('options', err),
      error: err,
    });
  toastError(err instanceof Error ? err.message : t('Failed to open Upload Modal.'));
}

// --- Uppy ---

/** Backend restrictions, then the single-file flag, then the caller's own (caller keys win). */
export function buildUploadRestrictions(
  opts: Record<string, unknown>,
  restrictToSingleFile: boolean | undefined,
  override: Partial<Restrictions> | undefined,
): Restrictions {
  return {
    ...(opts.restrictions as Restrictions),
    ...(restrictToSingleFile && { maxNumberOfFiles: 1 }),
    ...override,
  };
}

/** Uppy's locale for the app language, with Uppy's raw-MIME type error replaced by
 *  `unsupportedTypeMessage`. Pass it to the Uppy constructor (see `createUploadUppy`). */
export function buildUppyLocale(language: string, unsupportedTypeMessage: string) {
  const baseLocale = UPPY_LOCALE_MAP[language] || en_US;
  return {
    ...baseLocale,
    strings: { ...baseLocale.strings, youCanOnlyUploadFileTypes: unsupportedTypeMessage },
  };
}

/** Install the camera and cloud sources in `providers` into the Dashboard. On Transforge the
 *  backend sets `companion_url` (our Companion, which supports Google Drive); otherwise
 *  Transloadit's Companion. */
export function installUploadSources(
  uppy: Uppy,
  opts: Record<string, unknown>,
  providers: string[],
): void {
  const companionUrl = (opts.companion_url as string | undefined) || COMPANION_URL;
  const companionAllowedHosts =
    (opts.companion_allowed_hosts as string | undefined) || COMPANION_ALLOWED_HOSTS;
  if (providers.includes(UPLOAD_SOURCE.WEBCAM)) {
    uppy.use(Webcam, { target: Dashboard });
  }
  if (providers.includes(UPLOAD_SOURCE.GOOGLE_DRIVE)) {
    uppy.use(GoogleDrive, {
      target: Dashboard,
      companionUrl,
      companionAllowedHosts,
    });
  }
  if (providers.includes(UPLOAD_SOURCE.DROPBOX)) {
    uppy.use(Dropbox, {
      target: Dashboard,
      companionUrl,
      companionAllowedHosts,
    });
  }
  if (providers.includes(UPLOAD_SOURCE.ONEDRIVE)) {
    uppy.use(OneDrive, {
      target: Dashboard,
      companionUrl,
      companionAllowedHosts,
    });
  }
}

interface CreateUploadUppyArgs {
  opts: Record<string, unknown>;
  restrictions: Restrictions;
  language: string;
  t: TFunction;
  /** Mode-specific Dashboard options (inline/target/note/size…), merged over the shared ones. */
  dashboard: DashboardOptions;
  /** Cloud / camera sources to install (from `opts.providers`, possibly filtered by the caller). */
  providers: string[];
  /** Read at check time: the account may allow images over the resolution limit. */
  isImageResolutionLimitDisabled: () => boolean;
  /** A file refused by the client-side checks; it is already removed from Uppy. */
  onFileRejected: (file: UppyFile, reason: ClientRejectionReason, message: string) => void;
  /** Per file, once its assembly upload succeeded. */
  onUploadSuccess: (fileMeta: UploadFileMeta, assemblyId: string, bucket?: string) => void;
}

/** Build an Uppy instance with Dashboard + Transloadit/Transforge + providers + file checks. */
export function createUploadUppy({
  opts,
  restrictions,
  language,
  t,
  dashboard,
  providers,
  isImageResolutionLimitDisabled,
  onFileRejected,
  onUploadSuccess,
}: CreateUploadUppyArgs): Uppy {
  // Locale goes in the constructor, not a later setOptions(): Uppy's Restricter keeps the
  // translator it was built with, so a post-construction locale never reaches restriction
  // errors. Uppy's own type-restriction error also lists every allowed MIME type, so swap in
  // a plain-language message (#2551).
  const uppy = new Uppy({
    restrictions,
    locale: buildUppyLocale(
      language,
      getUnsupportedFileTypeMessage(restrictions.allowedFileTypes ?? undefined, t),
    ),
    allowMultipleUploads: false,
    onBeforeUpload: (files) => {
      // Inject fileID into meta for each file (scio1/scio2 pattern)
      const updated: typeof files = {};
      for (const [fileID, file] of Object.entries(files)) {
        // Sync the (possibly user-edited) meta.name onto the upload's name: server-side type
        // detection AND the completion pingback both derive from the upload name's extension,
        // so the edited name must be synced onto the upload and must keep an extension (#1786 +
        // user-testing regression). A rename with no dot at all inherits the original file's
        // extension; a rename containing a dot is trusted as an explicit user extension. The
        // normalized value goes to BOTH name and meta.name so the Uppy display, the assembly
        // fields.name, and the saved originalFileName all agree.
        const editedName = (file.meta?.name as string) || file.name;
        const name =
          !editedName.includes('.') && file.name.includes('.')
            ? `${editedName}${file.name.slice(file.name.lastIndexOf('.'))}`
            : editedName;
        updated[fileID] = {
          ...file,
          name,
          meta: { ...file.meta, fileID, name },
        };
      }
      return updated;
    },
  });

  const isDark = document.documentElement.classList.contains('dark');
  uppy.use(Dashboard, {
    proudlyDisplayPoweredByUppy: false,
    theme: isDark ? 'dark' : 'light',
    metaFields: [{ id: 'name', name: t('File name'), placeholder: t('Enter file name') }],
    ...dashboard,
  });

  // Transforge migration (USE_TRANSFORGE on the backend): when enabled, the
  // backend (open-api getUploadOptions) injects `endpoint` (Transforge gateway)
  // and `companion_url` (our self-hosted Companion) into opts. When absent, these
  // stay undefined and Uppy falls back to Transloadit SaaS + Transloadit Companion.
  // `service` is a plugin-level option (NOT part of assemblyOptions).
  const tfService = (opts.endpoint as string | undefined) || undefined;

  // Transloadit — 1 assembly per file.
  // In Uppy v3, assemblyOptions is called per-file. The backend returns
  // fields: ["name", "fileID"] as an array. Uppy resolves each field name
  // from file.meta, producing unique values per file (dedupe creates separate assemblies).
  uppy.use(Transloadit, {
    // Transforge gateway when the backend sets opts.endpoint; else Transloadit SaaS.
    ...(tfService ? { service: tfService } : {}),
    assemblyOptions: (_file: UppyFile): AssemblyOptions => {
      // Backend returns fields as an array of meta field names, e.g. ["name", "fileID"].
      // Uppy's Transloadit plugin resolves arrays by extracting values from file.meta
      // per file (see getFields in AssemblyOptions.js). This produces unique field values
      // per file (different name, different fileID), ensuring 1 assembly per file.
      // onBeforeUpload already injects fileID into file.meta for this to work.
      // IMPORTANT: Do NOT spread the array into an object — that produces { "0": "name", "1": "fileID" }.
      return structuredClone(opts) as AssemblyOptions;
    },
    waitForEncoding: false,
    waitForMetadata: false,
    // NOT just upload concurrency. @uppy/transloadit 3.x shares ONE RateLimitedQueue(limit)
    // between assembly status polling (Assembly.js, priority 0) and Companion token requests
    // (@uppy/tus, priority -1). Polling only stops once the assembly status socket connects
    // (Assembly.js: `if (!this.socket || !this.socket.connected) fetchStatus()`), and on the
    // Transforge gateway it never does — measured 6.5 poll req/s sustained for 8 min, which
    // pins both slots and starves the priority -1 token requests: 49 Drive files sat at 0%
    // for 7m40s, then drained in 73s. Need limit/0.3s > N/2s → >7.5 at N=50; 20 is the
    // plugin default and survives poll latency doubling. Do not lower it to throttle
    // uploads — that throttles polling too, and polling outranks uploads.
    //
    // Transforge only. Transloadit SaaS keeps 2: its polling is not known to run unbounded,
    // it is the path most accounts still use, and we have no measurement there to justify
    // 10x-ing their upload concurrency. Raised 2->5 globally once (2d018c04e) and reverted
    // (d5a1f9a5); 5 was under the bar anyway.
    limit: tfService ? 20 : 2,
  });

  installUploadSources(uppy, opts, providers);

  // --- Events (follows scio2 _openUploadModal pattern) ---

  // Client-side validation on file add
  uppy.on('file-added', async (file: UppyFile) => {
    const reject = (reason: ClientRejectionReason, message: string) => {
      uppy.removeFile(file.id);
      onFileRejected(file, reason, message);
    };

    // Empty file: the gateway only finds out AFTER the upload completes and then fails the
    // assembly with INPUT_FILE_EMPTY (400), so the user sees a raw server error and a dead
    // asset instead of a reason. Reject it here, before an assembly is created.
    // `file.size` (not `blob.size`) — for cloud-provider picks `file.data` is a stub and the
    // size comes from the provider. Strict `=== 0` on purpose: providers report NO size for
    // Google native files (Docs/Sheets/Slides), and null/undefined must not count as empty.
    if (file.size === 0) {
      reject(
        REJECTION_REASON.EMPTY,
        t('{{name}} is empty (0 bytes) and was skipped.', { name: file.name }),
      );
      return;
    }

    // Resolution / bitrate need the real file. A cloud pick (Google Drive, Dropbox, …) is only
    // a stub here (no type, nothing to decode); the server checks it after the import.
    if (file.isRemote) return;
    const blob = file.data as File;

    if (!isImageResolutionLimitDisabled()) {
      const resolutionError = await validateImageResolution(blob);
      if (resolutionError) {
        reject(REJECTION_REASON.RESOLUTION, resolutionError.message);
        return;
      }
    }

    const bitrateError = await validateVideoBitrate(blob);
    if (bitrateError) {
      reject(REJECTION_REASON.BITRATE, bitrateError.message);
    }
  });

  // Per-file failure — the only place the transport / assembly error OBJECT is visible
  // (`complete.failed` only carries file names, so the toast stays with the hook's `complete`
  // handler and the telemetry lives here). The provider's assembly error code rides along as
  // `code`. The user's own file (too large, unsupported, …) is not reported: the UI covers
  // them and the upload-processor lane holds the denominator.
  uppy.on('upload-error', (_file, error) => {
    if (isUserFileUploadError(error)) return;
    reportAppError({
      evt: 'upload_error',
      flow: 'upload-assembly',
      ...classifyUploadError('assembly', error),
      error,
    });
  });

  // Per-file success — extract assembly ID and call the caller's callback.
  // scio2: uppy.on('upload-success', (upload) => { _processUploads([upload], ...) })
  const bucket = (opts.bucket as string) || undefined;
  uppy.on('upload-success', (file: UppyFile | undefined) => {
    if (!file) return;
    const fileWithTransloadit = file as UppyFile & {
      transloadit?: { assembly?: string };
    };
    const assemblyId = fileWithTransloadit.transloadit?.assembly || '';

    // Use meta.name (the user's rename) over the original file.name
    const name = (file.meta?.name as string) || file.name || '';
    const extension = name.includes('.') ? name.split('.').pop() || '' : '';

    onUploadSuccess(
      {
        name,
        type: file.type || '',
        size: file.size || 0,
        extension,
      },
      assemblyId,
      bucket,
    );
  });

  return uppy;
}

/** Add local files (picked or dropped). Uppy throws on a restriction after emitting
 *  `restriction-failed`, which the hook's own UI reports. */
export function addLocalFiles(uppy: Uppy, files: File[]): void {
  for (const file of files) {
    try {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        source: 'Local',
        isRemote: false,
      });
    } catch {
      // Reported through `restriction-failed` (dialog rows) or Uppy's informer (legacy modal).
    }
  }
}
